import React, { FC } from 'react';

import {
  AddContributorIcon,
  ArrowIcon,
  CloseCircleIconSmall,
  CrossIcon,
  EditIcon,
  EyeIconSmall,
  GrantAccessIcon,
  RevokeAccessIcon,
  SendInviteIcon,
  SuccessCircleIcon,
  ThreeDotsIcon,
  TrashBinIcon,
} from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import { DropDown } from '../../components/dropdown/index';
import { FormattedDateDistance } from '../../components/formatted-date-distance/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../components/popover/index';
import { Tooltip } from '../../components/tooltip/index';
import { useFormat, useLocale } from '../../hooks/index';
import { stakeholderTypeTranslation } from '../../translations/stakeholder-translation.util';
import {
  Stakeholder,
  stakeholderExTypeTitle,
  StakeholderRole,
  TerminationCauseTitle,
} from '../../types/stakeholder.types';
import { toUpperCaseWords } from '../../utils/to-uppercase-util.util';
import { getStakeholderCommonActions } from './stakeholder-permissions.const';
import StakeholderStatus, { StakeholderActionStatus } from './stakeholder-status';

type StakeholderItemProps = {
  accountId: string;
  handleCancelInvitation: (id: string) => void;
  handleDelete: (id: string) => void;
  handleEdit: (id: string) => void;
  handleGrantAccess: (id: string) => void;
  handleMakeContributor: (id: string) => void;
  handleRemoveContributorRole: (id: string) => void;
  handleResendInvitation: (id: string) => void;
  handleRevokeAccess: (id: string) => void;
  handleSendInvitation: (id: string) => void;
  handleView: (id: string) => void;
  lastActive?: string;
  roleInCompany?: StakeholderRole;
  terminatedCard?: boolean;
  votingRightsPercentage?: number;
} & Stakeholder;

export const StakeholderItem: FC<StakeholderItemProps> = ({
  isAccessRevoked,
  role,
  type,
  isActive,
  isInvited,
  lastActive,
  votingRightsPercentage = 0,
  fullName,
  email,
  id,
  isTerminated,
  terminationCause,
  terminationDate,
  terminatedCard,
  lastVestingDate,
  subEmails,
  handleView,
  handleEdit,
  handleDelete,
  handleCancelInvitation,
  handleGrantAccess,
  handleMakeContributor,
  handleRemoveContributorRole,
  handleResendInvitation,
  handleRevokeAccess,
  handleSendInvitation,
  roleInCompany,
  accountId,
  hasShares,
  hasPendingSignatures,
}) => {
  const { messagesLocale } = useLocale();
  const {
    canDelete,
    cancelInvitation,
    grantAccess,
    makeContributor,
    resendInvitation,
    revokeAccess,
    revokeContributor,
  } = getStakeholderCommonActions({
    role,
    isInvited,
    isActive,
    isAccessRevoked,
  });

  const { format } = useFormat();

  return (
    <div className="flex h-fit w-full min-w-[300px] max-w-[440px] flex-col divide-y-[1px] divide-gray-100 rounded-md shadow-sm">
      <div className="flex items-center justify-between px-3 py-4">
        <div className="flex gap-3">
          <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
            {fullName[0]}
            {isTerminated && (
              <div className="absolute bottom-0 right-0 flex size-[14px] shrink-0 items-center justify-center rounded-full border-[1px] border-white bg-fireside-600">
                <CrossIcon className="size-[10px]" iconColor="#ffffff" />
              </div>
            )}
          </div>
          <div className="flex h-full flex-col justify-between">
            {accountId === id ? (
              <div className="flex items-center gap-1">
                <span className="max-w-[155px] truncate text-sm font-[450] text-gray-700">
                  {fullName}
                </span>
                <span className="text-sm font-[450] text-gray-700">
                  (<AppFormattedMessage id="YOU" />)
                </span>
              </div>
            ) : (
              <span className="max-w-[200px] truncate text-sm font-[450] text-gray-700">
                {fullName}
              </span>
            )}
            <span className="max-w-[200px] truncate text-sm text-gray-700">{email}</span>
          </div>
        </div>
        <Popover>
          <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
            <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
              <ThreeDotsIcon />
            </div>
          </PopoverTrigger>
          <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl">
            <PopoverClose>
              <div
                className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => handleView(id)}
              >
                <EyeIconSmall className="size-4" />
                <span className="text-sm font-normal text-gray-700">
                  <AppFormattedMessage id="VIEW_DETAILS" />
                </span>
              </div>
              {(role !== StakeholderRole.ADMIN || roleInCompany === StakeholderRole.ADMIN) && (
                <div
                  className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  data-permission-required
                  onClick={() => handleEdit(id)}
                >
                  <EditIcon className="size-4" />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="EDIT_DETAILS" />
                  </span>
                </div>
              )}

              {resendInvitation && (
                <div
                  className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  data-permission-required
                  onClick={() => handleResendInvitation(id)}
                >
                  <SendInviteIcon className="size-4" />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="RESEND_INVITATION" />
                  </span>
                </div>
              )}
              {cancelInvitation && (
                <div
                  className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  data-permission-required
                  onClick={() => handleCancelInvitation(id)}
                >
                  <CloseCircleIconSmall className="size-4" iconColor="#344054" />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="CANCEL_INVITATION" />
                  </span>
                </div>
              )}
              {grantAccess && (
                <div
                  className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  data-permission-required
                  onClick={() => handleGrantAccess(id)}
                >
                  <GrantAccessIcon className="size-4" />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="GRANT_ACCESS" />
                  </span>
                </div>
              )}

              {revokeAccess && accountId !== id && (
                <Tooltip
                  className="w-[300px] max-w-[300px]"
                  content={
                    hasPendingSignatures ? (
                      <AppFormattedMessage id="REVOKE_ACCESS_PENDING_SIGNATURES_TOOLTIP" />
                    ) : undefined
                  }
                >
                  <div
                    className={`flex w-full items-center gap-2 p-[6px] pr-8 text-sm hover:bg-gray-50 ${
                      hasPendingSignatures
                        ? 'cursor-not-allowed text-gray-300 hover:bg-white'
                        : 'cursor-pointer text-gray-700'
                    }`}
                    data-permission-required
                    onClick={() => {
                      if (hasPendingSignatures) return;
                      handleRevokeAccess(id);
                    }}
                  >
                    <RevokeAccessIcon
                      className="size-4"
                      iconColor={hasPendingSignatures ? '#D0D5DD' : undefined}
                    />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage id="REVOKE_ACCESS" />
                    </span>
                  </div>
                </Tooltip>
              )}
              {makeContributor && (
                <div
                  className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  data-permission-required
                  onClick={() => handleMakeContributor(id)}
                >
                  <AddContributorIcon className="size-4" />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="MAKE_CONTRIBUTOR" />
                  </span>
                </div>
              )}
              {revokeContributor && accountId !== id && (
                <div
                  className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  data-permission-required
                  onClick={() => handleRemoveContributorRole(id)}
                >
                  <RevokeAccessIcon className="size-4" />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="REVOKE_CONTRIBUTOR_ROLE" />
                  </span>
                </div>
              )}
              {canDelete && accountId !== id && !hasShares && (
                <div
                  className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-fireside-500 hover:bg-gray-50"
                  data-permission-required
                  onClick={() => handleDelete(id)}
                >
                  <TrashBinIcon className="size-4" iconColor="#F04438" />
                  <span className="text-sm font-normal text-fireside-500">
                    <AppFormattedMessage id="DELETE" />
                  </span>
                </div>
              )}
            </PopoverClose>
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex w-full justify-between p-3 pr-4">
        <span className="text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="TYPE" />
        </span>
        <span className="text-sm font-[450] text-gray-700">
          {isTerminated
            ? stakeholderExTypeTitle[type]
            : toUpperCaseWords(stakeholderTypeTranslation[type])}
        </span>
      </div>
      <div className="flex w-full flex-wrap justify-between gap-4 p-3 pr-4">
        {terminatedCard ? (
          <>
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="TERMINATION_TYPE" />
            </span>
            <span className="text-sm font-[450] text-gray-700">
              {terminationCause && (
                <AppFormattedMessage id={TerminationCauseTitle[terminationCause]} />
              )}
            </span>
          </>
        ) : (
          <>
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="VOTING_RIGHTS" />
            </span>
            <span className="text-sm font-[450] text-gray-700">
              {votingRightsPercentage
                ? parseFloat(votingRightsPercentage.toFixed(2))
                    .toString()
                    .replace(/\.?0+$/, '')
                : 0}
              %
            </span>
          </>
        )}
      </div>
      <div className="flex w-full justify-between p-3 pr-4">
        {terminatedCard ? (
          <>
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="TERMINATION_DATE" />
            </span>
            <span className="text-sm font-[450] text-gray-700">
              {terminationDate && format(terminationDate, 'dd/MM/yyyy')}
            </span>
          </>
        ) : (
          <>
            <span className="whitespace-nowrap text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="LAST_ACTIVE" />
            </span>
            <span className="text-sm font-[450] text-gray-700">
              {isActive ? <FormattedDateDistance date={new Date(lastActive)} /> : '-'}
            </span>
          </>
        )}
      </div>
      {terminatedCard && (
        <div className="flex w-full justify-between p-3 pr-4">
          <span className="text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="LAST_VESTING_DATE" />
          </span>
          <span className="text-sm font-[450] text-gray-700">
            {lastVestingDate && format(lastVestingDate, 'dd/MM/yyyy')}
          </span>
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto p-3">
        <StakeholderStatus
          type={isActive ? StakeholderActionStatus.ACTIVE : StakeholderActionStatus.INACTIVE}
        />
        {isActive && <StakeholderStatus type={role} />}

        {!isActive && (
          <>
            {isAccessRevoked && <StakeholderStatus type={StakeholderActionStatus.ACCESS_REVOKED} />}
            {isInvited ? (
              !isAccessRevoked && (
                <StakeholderStatus
                  className="flex-row-reverse"
                  icon={<SuccessCircleIcon />}
                  type={StakeholderActionStatus.INVITED}
                />
              )
            ) : (
              <StakeholderStatus
                className="cursor-pointer"
                icon={<ArrowIcon className={'rotate-180'} iconColor="#344054" />}
                onClick={() => handleSendInvitation(id)}
                type={StakeholderActionStatus.INVITE_USER}
              />
            )}
          </>
        )}
      </div>
      {Boolean(subEmails?.length) && (
        <DropDown
          answer={
            <div className="flex flex-col divide-y divide-[#F2F4F7] bg-white text-sm text-[#667085]">
              {subEmails?.map((subEmail) => (
                <span className="p-3" key={subEmail}>
                  {subEmail}
                </span>
              ))}
            </div>
          }
          className={{
            questionIconMonoColor: true,
            questionLabelWrapper: 'p-3',
            questionLabel: 'text-sm text-[#2565C8]',
            wrapper: 'rounded-t-none bg-[#F4F8FF] p-0',
            answerWrapper: 'mt-0',
            questionIcon: '-rotate-90',
          }}
          question={messagesLocale['VIEW_ASSOCIATED_STAKEHOLDER']}
        />
      )}
    </div>
  );
};
